import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { AlertCircle, Building2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useCreateOrganization } from '@/hooks/useCreateOrganization';
import { useRejoinOrganization } from '@/hooks/useRejoinOrganization';

/**
 * Where `OnboardedOutlet` sends anyone whose account resolves to no
 * organization. Signup provisions one, so the usual arrival here is a user who
 * lost their last team membership: organization membership is derived purely
 * from team membership, and dropping the last one leaves the account intact but
 * attached to nothing.
 *
 * This page used to be an inert "contact support" notice whose only button
 * linked to /home — which `OnboardedOutlet` bounced straight back here. Giving
 * it the real create-organization mutation makes it an exit rather than a loop.
 */
export default function CreateOrganization() {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const createOrg = useCreateOrganization();
  const rejoinOrg = useRejoinOrganization();

  // Provisioning sets the new org's context server-side and the hook refetches
  // the user, so a successful create lands here with an organization and leaves.
  if (user?.organizationId) {
    return <Navigate to="/home" replace />;
  }

  const trimmed = name.trim();
  const ownedOrganizationId = user?.ownedOrganizationId;

  // Ownership is capped at one org per user, so for someone who already owns the
  // organization they've been detached from, POST /api/organizations can only
  // 400. Offering the create form would be offering a button that cannot work —
  // what they need is back into the org they already own.
  if (ownedOrganizationId) {
    return (
      <main className="px-4 md:px-6 py-12 max-w-md mx-auto w-full">
        <Alert variant="default" className="border-sev-3 bg-sev-3-soft">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-1.5">You're not on any team</p>
            <p className="text-sm">
              You own an organization, but you're no longer a member of any of its
              teams — and access comes from team membership.
            </p>
          </AlertDescription>
        </Alert>

        {/* If the organization has no members left there is no administrator to
            ask, so offer the recovery directly. When it does still have members
            the request is refused, and that refusal says who to ask. */}
        <Button
          className="w-full mt-4"
          onClick={() => { rejoinOrg.mutate(ownedOrganizationId); }}
          disabled={rejoinOrg.isPending}
        >
          {rejoinOrg.isPending ? 'Restoring access…' : 'Rejoin your organization'}
        </Button>

        {rejoinOrg.isError ? (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {rejoinOrg.error.response?.data.description ??
                rejoinOrg.error.response?.data.error ??
                'Could not restore access. Contact support.'}
            </AlertDescription>
          </Alert>
        ) : null}
        <p className="text-center mt-4">
          <Link
            to="/signout"
            className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
          >
            Sign out
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="px-4 md:px-6 py-12 max-w-md mx-auto w-full">
      <div className="flex flex-col items-center text-center mb-6">
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
          <Building2 className="h-5 w-5 text-muted-foreground" />
        </div>
        <h1 className="text-lg font-semibold">Create your organization</h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Your account isn't part of an organization yet. Create one to get
          started — you'll own it, and you'll be its first administrator.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed.length < 2) return;
          createOrg.mutate(trimmed);
        }}
        className="space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="co-name">Organization name</Label>
          <Input
            id="co-name"
            value={name}
            onChange={(e) => { setName(e.target.value); }}
            placeholder="Acme Networks"
            autoFocus
            required
            minLength={2}
            disabled={createOrg.isPending}
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={trimmed.length < 2 || createOrg.isPending}
        >
          {createOrg.isPending ? 'Creating…' : 'Create organization'}
        </Button>
      </form>

      <p className="text-center mt-6">
        <Link
          to="/signout"
          className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Sign out
        </Link>
      </p>
    </main>
  );
}
